import { useEffect } from "react";
import { useFormContext } from "react-hook-form";

import { PRICE_DIMENSION, TPriceDimension } from "@/features/organization/product/constants/productEnums";
import { TProductForm, TTypePriceForm } from "@/api/services/main/organization/product/dtos/product.dto";

type TUseResetOrphanedCombinationsParams = {
  /** Current 種別単価 rows (watched) — the source of the valid nationality / field / tier references. */
  typePrices: TTypePriceForm[];
  /** ティア / その他 — which dimension the combination's third column references. */
  thirdDimension: TPriceDimension;
};

/**
 * Keep 単価組み合わせ rows consistent with 種別単価: when a nationality / field / tier(other) 種別単価 row
 * is removed (or its value cleared), reset that field on any combination that still points at it — so a
 * combination never carries a dangling reference. Runs on 種別単価 changes only (deletion happens there);
 * combination edits can't create an orphan.
 */
export const useResetOrphanedCombinations = ({ typePrices, thirdDimension }: TUseResetOrphanedCombinationsParams): void => {
  const { getValues, setValue } = useFormContext<TProductForm>();

  useEffect(() => {
    const validNationalityIds = new Set<string>();
    const validFieldIds = new Set<string>();
    const validVariantTypeIds = new Set<string>();

    for (const row of typePrices) {
      if (row.isDefault) continue;
      if (row.dimension === PRICE_DIMENSION.NATIONALITY && row.nationalityId) validNationalityIds.add(row.nationalityId);
      if (row.dimension === PRICE_DIMENSION.FIELD && row.fieldId) validFieldIds.add(row.fieldId);
      // Keyed on row existence (id), not text, so only deletion — not a mid-edit empty name — resets it.
      if (row.dimension === thirdDimension) validVariantTypeIds.add(row.id);
    }

    (getValues("combinations") ?? []).forEach((row, index) => {
      if (row.nationalityId && !validNationalityIds.has(row.nationalityId)) {
        setValue(`combinations.${index}.nationalityId`, null, { shouldDirty: false });
      }
      if (row.fieldId && !validFieldIds.has(row.fieldId)) {
        setValue(`combinations.${index}.fieldId`, null, { shouldDirty: false });
      }
      if (row.variantTypeId && !validVariantTypeIds.has(row.variantTypeId)) {
        setValue(`combinations.${index}.variantTypeId`, null, { shouldDirty: false });
      }
    });
  }, [typePrices, thirdDimension, getValues, setValue]);
};
